import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, Clock, TrendingUp, Calendar, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AgentDashboard = ({ agent }) => {
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    totalTasks: 0,
    pendingTasks: 0,
    completedTasks: 0,
    totalCommunications: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (agent?.agentId) {
      fetchAgentPerformance();
    }
  }, [agent]);

  const fetchAgentPerformance = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/agent-performance/${agent.agentId}`);
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
        
        // Combine recent activities
        const activities = [];
        if (data.recentActivities.notes) {
          data.recentActivities.notes.forEach(note => {
            activities.push({
              type: 'note',
              title: 'Added Note',
              description: note.note.substring(0, 100) + '...',
              createdAt: note.createdAt,
              icon: '📝'
            });
          });
        }
        if (data.recentActivities.tasks) {
          data.recentActivities.tasks.forEach(task => {
            activities.push({
              type: 'task',
              title: task.title,
              description: `${task.taskType} - ${task.priority} priority`,
              createdAt: task.createdAt,
              icon: '✅'
            });
          });
        }
        if (data.recentActivities.communications) {
          data.recentActivities.communications.forEach(comm => {
            activities.push({
              type: 'communication',
              title: `${comm.communicationType.charAt(0).toUpperCase() + comm.communicationType.slice(1)} - ${comm.outcome}`,
              description: comm.summary.substring(0, 100),
              createdAt: comm.createdAt,
              icon: '📞'
            });
          });
        }
        
        // Sort by date
        activities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setRecentActivities(activities.slice(0, 10));
      }
    } catch (error) {
      console.error('Error fetching performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">Welcome back, {agent.name}! 👋</h2>
        <p className="text-blue-100">Here's your performance overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Total Clients</p>
            <p className="text-3xl font-bold text-slate-800">{stats.totalClients}</p>
            <p className="text-sm text-green-600 mt-2">{stats.activeClients} active</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Completed Tasks</p>
            <p className="text-3xl font-bold text-slate-800">{stats.completedTasks}</p>
            <p className="text-sm text-slate-500 mt-2">of {stats.totalTasks} total</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Pending Tasks</p>
            <p className="text-3xl font-bold text-slate-800">{stats.pendingTasks}</p>
            <p className="text-sm text-orange-600 mt-2">Needs attention</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Phone className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Communications</p>
            <p className="text-3xl font-bold text-slate-800">{stats.totalCommunications}</p>
            <p className="text-sm text-slate-500 mt-2">Total logged</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Total Revenue</p>
            <p className="text-3xl font-bold text-slate-800">₹{(stats.totalRevenue || 0).toLocaleString()}</p>
            <p className="text-sm text-slate-500 mt-2">From all clients</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-cyan-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Avg. Client Value</p>
            <p className="text-3xl font-bold text-slate-800">₹{Math.round(stats.avgClientValue || 0).toLocaleString()}</p>
            <p className="text-sm text-slate-500 mt-2">Per client</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Recent Activity</h3>
        </div>
        <div className="p-6">
          {recentActivities.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No recent activities</p>
          ) : (
            <div className="space-y-4">
              {recentActivities.map((activity, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="text-2xl">{activity.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800">{activity.title}</p>
                    <p className="text-sm text-slate-600 truncate">{activity.description}</p>
                  </div>
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    {formatDate(activity.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;